import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
} from '@angular/forms';
import { MatTab } from '@angular/material/tabs';
import { AppStateFacade } from '@app-state';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import {
    ModalResultType,
    UserType,
} from '@models';
import { ProvisionRobotComponent } from '@pages/users/components/provision-robot/provision-robot.component';
import {
    RoleService,
    UserService,
} from '@services';
import { ISuggestValue } from '@uipath/angular/components/ui-suggest';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    BehaviorSubject,
    Observable,
    of,
} from 'rxjs';
import {
    finalize,
    switchMap,
} from 'rxjs/operators';

export enum UserGroupField {
    Domain = 'domain',
    OrganizationUnits = 'organizationUnits',
    MayHaveRobotSession = 'mayHaveRobotSession',
    MayHaveUserSession = 'mayHaveUserSession',
    Robot = 'robotProvision',
    Roles = 'rolesList',
    Type = 'type',
    UserName = 'userName',
}
@Component({
    templateUrl: './user-group-import.modal.html',
    providers: [RoleService],
    styleUrls: ['./user-group-import.modal.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserGroupImportModalComponent
    extends UiModalComponent<any>
    implements OnInit {

    public readonly UserGroupField = UserGroupField;
    public readonly UserType = UserType;
    public readonly loading$ = new BehaviorSubject(false);
    public isRobotSwitcherEnabled$!: Observable<boolean>;

    public form!: FormGroup;
    public isAdSearchLoading = true;

    @ViewChild(ProvisionRobotComponent, { static: false })
    public set provisionRobot(value: ProvisionRobotComponent) {
        if (!value) {
            this.form.removeControl(UserGroupField.Robot);
            return;
        }

        this.form.addControl(
            UserGroupField.Robot,
            value.form,
        );
    }

    @ViewChild('generalTab', { static: false })
    public generalTab?: MatTab;

    @ViewChild('settingsTab', { static: false })
    public settingsTab?: MatTab;

    private _appState = this._injector.get<AppStateFacade>(AppStateFacade);
    private _userService = this._injector.get<UserService>(UserService);
    private _roleService = this._injector.get<RoleService>(RoleService);

    ngOnInit() {
        this.form = this._injector.get(FormBuilder).group({
            [UserGroupField.UserName]: [[]],
            [UserGroupField.Domain]: [[]],
            [UserGroupField.Type]: [],
            [UserGroupField.MayHaveUserSession]: [true],
            [UserGroupField.Roles]: [[]],
            [UserGroupField.OrganizationUnits]: [[]],
            [UserGroupField.MayHaveRobotSession]: [false],
        });

        this.isRobotSwitcherEnabled$ = this._appState.user.permissions.isGranted('Robots.Create');
    }

    public roleSearchFactory = (...args: any) =>
        this._appState.user.permissions
            .isGranted('Roles.View')
            .pipe(
                switchMap(result => result ?
                    this._roleService.suggest.apply(null, args) :
                    of({ data: [], total: 0 }),
                ),
            )

    public onSubmit() {
        if (
            !this.form.valid ||
            this.loading$.value
        ) { return; }

        this.loading$.next(true);

        const payload = cloneDeep(this.form.value);
        Object.assign(payload, {
            [UserGroupField.Domain]: this.form.get(UserGroupField.Domain)!.value[0].id,
            [UserGroupField.UserName]: this.form.get(UserGroupField.UserName)!.value[0].id,
            [UserGroupField.Roles]: this.form.get(UserGroupField.Roles)!
                .value.map((item: ISuggestValue) => item.text),
        });

        // FIXME: implement this in a better way
        if (payload.robotProvision) {
            payload.licenseType = payload.robotProvision.type;
            payload.isExternalLicensed = payload.robotProvision.isExternalLicensed;
            delete payload.robotProvision.type;
            delete payload.robotProvision.isExternalLicensed;
        }

        this._userService.add(payload)
            .pipe(
                finalize(
                    () => this.loading$.next(false),
                ),
            )
            .subscribe(
                () =>
                    this._dialogRef.close({
                        type: ModalResultType.Add,
                    }),
            );
    }
}
